/**
 * A pragmatic AsciiDoc parser covering the subset actually used by
 * NKBIP-01 "Curated Publications" content (kind 30041 sections) and by
 * AsciiDoc-flavored NIP-54 wiki articles in the wild — including, notably,
 * the official NKBIP-01 specification itself, which is written in AsciiDoc
 * and uses `=` / `==` heading markers rather than Djot's `#` / `##`.
 *
 * This is NOT a full AsciiDoc implementation (see https://asciidoctor.org
 * for that) — there is no small, dependency-free, browser-safe AsciiDoc
 * parser available (the reference implementation, Asciidoctor.js, is a
 * multi-hundred-KB port of a Ruby processor). Instead this covers the
 * document features that matter for wiki/publication content:
 *
 * Block level: document title / section headings (`=` through `======`),
 * paragraphs, bullet/ordered lists (incl. nesting), admonition paragraphs
 * (`NOTE:` / `TIP:` / `IMPORTANT:` / `WARNING:` / `CAUTION:`) and blocks
 * (`[NOTE]` + delimited block), block quotes (`____`), listing/source
 * blocks (`----`, with an optional `[source,lang]` attribute line),
 * literal blocks (`....`), sidebar/example blocks (`****` / `====`),
 * thematic breaks (`'''`), tables (`|===`), block images (`image::`),
 * and document attribute entries (`:name: value`, parsed but not
 * rendered).
 *
 * Inline level: `*strong*`, `_emphasis_`, `` `monospace` ``, `^superscript^`,
 * `~subscript~`, `#highlight#`, autolinks, `link:url[text]`,
 * `image:url[alt]`, hard line breaks (trailing ` +`), and double-bracket
 * wikilinks (`[[Target]]` / `[[Target,Display text]]`) per NKBIP-01's
 * "content MAY contain wikilinks (denoted by double brackets)".
 *
 * As with the Djot renderer, parsing never throws (falls back to an empty
 * document) and the resulting AST is rendered by `AsciiDocContent` without
 * ever touching `dangerouslySetInnerHTML` — AsciiDoc's own raw-passthrough
 * features (`pass:[...]`, `+++...+++`) are intentionally NOT implemented,
 * since they exist specifically to inject verbatim HTML.
 */

export type AdocInline =
  | { tag: 'text'; text: string }
  | { tag: 'strong'; children: AdocInline[] }
  | { tag: 'emphasis'; children: AdocInline[] }
  | { tag: 'monospace'; children: AdocInline[] }
  | { tag: 'superscript'; children: AdocInline[] }
  | { tag: 'subscript'; children: AdocInline[] }
  | { tag: 'highlight'; children: AdocInline[] }
  | { tag: 'link'; href: string; children: AdocInline[] }
  | { tag: 'image'; src: string; alt: string }
  | { tag: 'wikilink'; target: string; display?: string }
  | { tag: 'break' };

export interface AdocListItem {
  children: AdocInline[];
  /** Nested sub-list, if any (AsciiDoc nests by repeating the marker character, e.g. `**`). */
  sublist?: AdocBlock;
}

export type AdocBlock =
  | { tag: 'heading'; level: number; id: string; children: AdocInline[] }
  | { tag: 'paragraph'; children: AdocInline[] }
  | { tag: 'admonition'; kind: AdmonitionKind; children: AdocBlock[] }
  | { tag: 'quote'; attribution?: string; children: AdocBlock[] }
  | { tag: 'code'; lang?: string; text: string }
  | { tag: 'literal'; text: string }
  | { tag: 'thematic_break' }
  | { tag: 'bullet_list'; items: AdocListItem[] }
  | { tag: 'ordered_list'; items: AdocListItem[] }
  | { tag: 'table'; rows: AdocInline[][][]; header: boolean }
  | { tag: 'image_block'; src: string; alt: string; title?: string }
  | { tag: 'sidebar'; children: AdocBlock[] };

export type AdmonitionKind = 'note' | 'tip' | 'important' | 'warning' | 'caution';

export interface AdocDoc {
  title?: string;
  children: AdocBlock[];
}

const ADMONITION_KINDS: AdmonitionKind[] = ['note', 'tip', 'important', 'warning', 'caution'];
const ADMONITION_PREFIX_RE = /^(NOTE|TIP|IMPORTANT|WARNING|CAUTION):\s+(.*)$/;

function slugifyHeadingId(text: string, used: Set<string>): string {
  const base =
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s_-]/gu, '')
      .trim()
      .replace(/\s+/g, '-') || 'section';
  let id = base;
  let i = 1;
  while (used.has(id)) {
    id = `${base}-${i}`;
    i += 1;
  }
  used.add(id);
  return id;
}

/** Parses AsciiDoc markup into an AST. Never throws — falls back to an empty document on parse errors. */
export function parseAsciiDoc(source: string): AdocDoc {
  try {
    return parseDocument(source);
  } catch (error) {
    // Logged (not swallowed silently) so a bad AsciiDoc parse — e.g. a
    // stack overflow from a runaway recursive branch — is debuggable.
    // Callers should treat an empty result as a signal to fall back to
    // another renderer rather than showing nothing (see `WikiContent`'s
    // `isEmptyParse` safety net).
    console.warn('Failed to parse AsciiDoc content, rendering as empty:', error);
    return { children: [] };
  }
}

function parseDocument(source: string): AdocDoc {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const usedIds = new Set<string>();

  // Document title: a single `= Title` on the very first non-blank,
  // non-attribute line. This is tracked purely as metadata (e.g. for a
  // caller that wants to show it in a page `<title>`) — it is NOT
  // skipped/removed from the block stream below, so the heading is still
  // rendered like any other. Earlier versions of this parser consumed the
  // line here without re-adding it as a block, which silently dropped the
  // entire heading (and, for title-only content, the entire document)
  // from the rendered output.
  let firstContentLine = 0;
  while (firstContentLine < lines.length && (lines[firstContentLine].trim() === '' || isAttributeEntry(lines[firstContentLine]))) {
    firstContentLine += 1;
  }
  const titleMatch = firstContentLine < lines.length ? lines[firstContentLine].match(/^=\s+(.+)$/) : null;
  const title = titleMatch ? titleMatch[1].trim() : undefined;

  const { blocks } = parseBlocks(lines, 0, lines.length, usedIds);
  return { title, children: blocks };
}

function isAttributeEntry(line: string): boolean {
  return /^:[^:\s][^:]*:(\s.*)?$/.test(line.trim());
}

function isCommentLine(line: string): boolean {
  return /^\/\/(?!\/)/.test(line.trim());
}

interface BlockAttrs {
  style?: string; // e.g. "source", "quote", "NOTE", ".rolename" (leading style token)
  lang?: string; // for [source,lang]
  attribution?: string; // for [quote, Attribution, Citation]
}

function parseBlockAttrLine(line: string): BlockAttrs | undefined {
  const m = line.trim().match(/^\[([^\]]*)\]$/);
  if (!m) return undefined;
  const parts = m[1].split(',').map((s) => s.trim());
  const style = parts[0];
  if (style?.toLowerCase() === 'source') {
    return { style: 'source', lang: parts[1] };
  }
  if (style?.toLowerCase() === 'quote') {
    return { style: 'quote', attribution: parts.slice(1).filter(Boolean).join(', ') };
  }
  return { style };
}

/** Parses a run of lines (block-level) between `start` (inclusive) and `end` (exclusive). */
function parseBlocks(lines: string[], start: number, end: number, usedIds: Set<string>): { blocks: AdocBlock[] } {
  const blocks: AdocBlock[] = [];
  let i = start;
  let pendingAttrs: BlockAttrs | undefined;

  while (i < end) {
    const line = lines[i];

    if (line.trim() === '') {
      i += 1;
      continue;
    }
    if (isCommentLine(line) || isAttributeEntry(line)) {
      i += 1;
      continue;
    }

    const attrLine = parseBlockAttrLine(line);
    if (attrLine) {
      pendingAttrs = attrLine;
      i += 1;
      continue;
    }

    // Thematic break.
    if (/^'{3,}\s*$/.test(line)) {
      blocks.push({ tag: 'thematic_break' });
      pendingAttrs = undefined;
      i += 1;
      continue;
    }

    // Heading: 1-6 `=` characters, a space, then text.
    const headingMatch = line.match(/^(={1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2].trim();
      const id = slugifyHeadingId(text, usedIds);
      blocks.push({ tag: 'heading', level, id, children: parseInline(text) });
      pendingAttrs = undefined;
      i += 1;
      continue;
    }

    // Admonition block: `[NOTE]` (etc.) followed by a delimited block.
    if (pendingAttrs?.style && ADMONITION_KINDS.includes(pendingAttrs.style.toLowerCase() as AdmonitionKind)) {
      const kind = pendingAttrs.style.toLowerCase() as AdmonitionKind;
      const delim = matchDelimiter(line);
      if (delim) {
        const closeAt = findDelimiterClose(lines, i + 1, end, delim);
        const inner = parseBlocks(lines, i + 1, closeAt, usedIds);
        blocks.push({ tag: 'admonition', kind, children: inner.blocks });
        pendingAttrs = undefined;
        i = closeAt + 1;
        continue;
      }
    }

    // Admonition paragraph: `NOTE: text`.
    const admonitionMatch = line.match(ADMONITION_PREFIX_RE);
    if (admonitionMatch) {
      const kind = admonitionMatch[1].toLowerCase() as AdmonitionKind;
      const { text: paraText, next } = collectParagraphLines(lines, i, end, admonitionMatch[2]);
      blocks.push({ tag: 'admonition', kind, children: [{ tag: 'paragraph', children: parseInline(paraText) }] });
      pendingAttrs = undefined;
      i = next;
      continue;
    }

    // Listing / source block: `----`.
    if (/^-{4,}\s*$/.test(line)) {
      const closeAt = findLineDelimiterClose(lines, i + 1, end, /^-{4,}\s*$/);
      const text = lines.slice(i + 1, closeAt).join('\n');
      blocks.push({ tag: 'code', lang: pendingAttrs?.lang, text });
      pendingAttrs = undefined;
      i = closeAt + 1;
      continue;
    }

    // Literal block: `....`.
    if (/^\.{4,}\s*$/.test(line)) {
      const closeAt = findLineDelimiterClose(lines, i + 1, end, /^\.{4,}\s*$/);
      const text = lines.slice(i + 1, closeAt).join('\n');
      blocks.push({ tag: 'literal', text });
      pendingAttrs = undefined;
      i = closeAt + 1;
      continue;
    }

    // Quote block: `____`.
    if (/^_{4,}\s*$/.test(line)) {
      const closeAt = findLineDelimiterClose(lines, i + 1, end, /^_{4,}\s*$/);
      const inner = parseBlocks(lines, i + 1, closeAt, usedIds);
      blocks.push({ tag: 'quote', attribution: pendingAttrs?.attribution, children: inner.blocks });
      pendingAttrs = undefined;
      i = closeAt + 1;
      continue;
    }

    // Sidebar/example block: `****` or `====` (as a whole-line delimiter,
    // not a heading — headings require trailing text after a space).
    if (/^\*{4,}\s*$/.test(line) || /^={4,}\s*$/.test(line)) {
      const re = /^\*{4,}\s*$/.test(line) ? /^\*{4,}\s*$/ : /^={4,}\s*$/;
      const closeAt = findLineDelimiterClose(lines, i + 1, end, re);
      const inner = parseBlocks(lines, i + 1, closeAt, usedIds);
      blocks.push({ tag: 'sidebar', children: inner.blocks });
      pendingAttrs = undefined;
      i = closeAt + 1;
      continue;
    }

    // Open block: `--` alone on a line — treated as a generic container
    // (commonly paired with an admonition/quote style attribute above).
    if (/^--\s*$/.test(line)) {
      const closeAt = findLineDelimiterClose(lines, i + 1, end, /^--\s*$/);
      const inner = parseBlocks(lines, i + 1, closeAt, usedIds);
      if (pendingAttrs?.style && ADMONITION_KINDS.includes(pendingAttrs.style.toLowerCase() as AdmonitionKind)) {
        blocks.push({ tag: 'admonition', kind: pendingAttrs.style.toLowerCase() as AdmonitionKind, children: inner.blocks });
      } else {
        blocks.push({ tag: 'sidebar', children: inner.blocks });
      }
      pendingAttrs = undefined;
      i = closeAt + 1;
      continue;
    }

    // Table: `|===` ... `|===`.
    if (/^\|=+\s*$/.test(line)) {
      const closeAt = findLineDelimiterClose(lines, i + 1, end, /^\|=+\s*$/);
      const { rows, header } = parseTable(lines, i + 1, closeAt);
      blocks.push({ tag: 'table', rows, header });
      pendingAttrs = undefined;
      i = closeAt + 1;
      continue;
    }

    // Block image: `image::target[alt]`.
    const blockImageMatch = line.match(/^image::([^[\]\s][^[\]]*)\[([^\]]*)\]\s*$/);
    if (blockImageMatch) {
      blocks.push({ tag: 'image_block', src: blockImageMatch[1].trim(), alt: blockImageMatch[2].trim() });
      pendingAttrs = undefined;
      i += 1;
      continue;
    }

    // Lists (bullet or ordered), possibly nested.
    const listMatch = matchListMarker(line);
    if (listMatch) {
      const { block, next } = parseList(lines, i, end, listMatch.ordered, listMatch.markerLen);
      blocks.push(block);
      pendingAttrs = undefined;
      i = next;
      continue;
    }

    // Fallback: paragraph — a run of contiguous non-blank lines.
    const { text: paraText, next } = collectParagraphLines(lines, i, end);
    blocks.push({ tag: 'paragraph', children: parseInline(paraText) });
    pendingAttrs = undefined;
    i = next;
  }

  return { blocks };
}

/** Collects a paragraph's lines starting at `start`, honoring AsciiDoc hard breaks (trailing ` +`). Returns joined display text (breaks marked with `\u0000` sentinel) and the index after the paragraph. */
function collectParagraphLines(lines: string[], start: number, end: number, firstLineOverride?: string): { text: string; next: number } {
  const out: string[] = [firstLineOverride ?? lines[start]];
  let i = start + 1;
  while (i < end) {
    const line = lines[i];
    if (line.trim() === '') break;
    if (isStructuralLineStart(line)) break;
    out.push(line);
    i += 1;
  }
  // Join with a sentinel so the inline parser can turn "line +\n" into a
  // hard break, and plain wrapped lines into a soft space.
  const joined = out
    .map((l) => (l.endsWith(' +') ? `${l.slice(0, -2)}\u0000` : l))
    .join('\n');
  return { text: joined, next: i };
}

/** True if `line` starts a new structural block (used to stop paragraph collection). */
function isStructuralLineStart(line: string): boolean {
  if (/^={1,6}\s+/.test(line)) return true;
  if (/^-{4,}\s*$/.test(line)) return true;
  if (/^\.{4,}\s*$/.test(line)) return true;
  if (/^_{4,}\s*$/.test(line)) return true;
  if (/^\*{4,}\s*$/.test(line)) return true;
  if (/^={4,}\s*$/.test(line)) return true;
  if (/^--\s*$/.test(line)) return true;
  if (/^\|=+\s*$/.test(line)) return true;
  if (/^'{3,}\s*$/.test(line)) return true;
  if (/^\[[^\]]*\]$/.test(line.trim())) return true;
  if (matchListMarker(line)) return true;
  if (ADMONITION_PREFIX_RE.test(line)) return true;
  if (/^image::/.test(line)) return true;
  return false;
}

function matchDelimiter(line: string): RegExp | undefined {
  if (/^-{4,}\s*$/.test(line)) return /^-{4,}\s*$/;
  if (/^\.{4,}\s*$/.test(line)) return /^\.{4,}\s*$/;
  if (/^_{4,}\s*$/.test(line)) return /^_{4,}\s*$/;
  if (/^\*{4,}\s*$/.test(line)) return /^\*{4,}\s*$/;
  if (/^={4,}\s*$/.test(line)) return /^={4,}\s*$/;
  if (/^--\s*$/.test(line)) return /^--\s*$/;
  return undefined;
}

function findLineDelimiterClose(lines: string[], start: number, end: number, re: RegExp): number {
  for (let i = start; i < end; i++) {
    if (re.test(lines[i])) return i;
  }
  return end;
}

function findDelimiterClose(lines: string[], start: number, end: number, re: RegExp): number {
  return findLineDelimiterClose(lines, start, end, re);
}

function matchListMarker(line: string): { ordered: boolean; markerLen: number } | undefined {
  const bullet = line.match(/^(\*{1,5}|-{1})\s+\S/);
  if (bullet) return { ordered: false, markerLen: bullet[1].length };
  const ordered = line.match(/^(\.{1,5})\s+\S/);
  if (ordered) return { ordered: true, markerLen: ordered[1].length };
  // Numeric ordered lists (`1.`, `2.`, …) are all treated as one level,
  // matching AsciiDoc's convention that the actual numbers are ignored.
  const numeric = line.match(/^\d+\.\s+\S/);
  if (numeric) return { ordered: true, markerLen: 1 };
  return undefined;
}

/** Parses a (possibly nested) list starting at `start`. */
function parseList(
  lines: string[],
  start: number,
  end: number,
  ordered: boolean,
  markerLen: number,
): { block: AdocBlock; next: number } {
  const items: AdocListItem[] = [];
  let i = start;

  const markerRe = ordered
    ? new RegExp(`^(?:\\.{${markerLen}}|\\d+\\.)\\s+(.*)$`)
    : new RegExp(`^(?:\\*{${markerLen}}|-)\\s+(.*)$`);

  while (i < end) {
    const line = lines[i];
    const m = line.match(markerRe);
    if (!m) break;

    // Collect continuation lines for this item (indented or wrapped text
    // up to the next marker at this or a shallower level, a blank line, or
    // a deeper-nested marker which becomes a sublist).
    const itemLines = [m[1]];
    let j = i + 1;
    let sublistStart = -1;
    while (j < end) {
      const next = lines[j];
      if (next.trim() === '') break;
      const nextMarker = matchListMarker(next);
      if (nextMarker) {
        if (nextMarker.ordered === ordered && nextMarker.markerLen === markerLen) break; // sibling item
        if (nextMarker.markerLen > markerLen || nextMarker.ordered !== ordered) {
          sublistStart = j;
          break;
        }
        break; // shallower marker — belongs to a parent list
      }
      if (isStructuralLineStart(next)) break;
      itemLines.push(next);
      j += 1;
    }

    let sublist: AdocBlock | undefined;
    let nextIndex = j;
    if (sublistStart !== -1) {
      const subMarker = matchListMarker(lines[sublistStart]);
      if (subMarker) {
        const parsed = parseList(lines, sublistStart, end, subMarker.ordered, subMarker.markerLen);
        sublist = parsed.block;
        nextIndex = parsed.next;
      }
    }

    items.push({ children: parseInline(itemLines.join('\n')), sublist });
    i = nextIndex;

    // Skip a single blank line between items (loose lists), but stop if
    // two blank lines or a new block follows.
    if (i < end && lines[i]?.trim() === '') {
      const afterBlank = i + 1;
      if (afterBlank < end && matchListMarker(lines[afterBlank])) {
        i = afterBlank;
      }
    }
  }

  const block: AdocBlock = ordered ? { tag: 'ordered_list', items } : { tag: 'bullet_list', items };
  return { block, next: i };
}

function parseTable(lines: string[], start: number, end: number): { rows: AdocInline[][][]; header: boolean } {
  const raw = lines.slice(start, end).join('\n');
  // Cells are separated by `|` at the start of a cell; AsciiDoc allows
  // multi-line cells, so split on `|` that begins a cell (preceded by
  // start-of-string, newline, or whitespace) rather than every `|`.
  const cellTexts = raw
    .split(/(?:^|\n)\s*\|/)
    .map((c) => c.trim())
    .filter((c) => c.length > 0);

  // We don't have reliable column-count metadata without a header
  // separator, so infer column count from the first row's cell count if
  // consistent, defaulting to a single column otherwise. This keeps
  // rendering correct for the common case (fixed column count throughout).
  const firstLineCells = (lines[start] ?? '').split('|').filter((s) => s.trim().length > 0).length;
  const columns = firstLineCells > 0 ? firstLineCells : 1;

  const rows: AdocInline[][][] = [];
  for (let i = 0; i < cellTexts.length; i += columns) {
    const rowCells = cellTexts.slice(i, i + columns).map((c) => parseInline(c));
    if (rowCells.length > 0) rows.push(rowCells);
  }

  return { rows, header: rows.length > 1 };
}

// ---------------------------------------------------------------------------
// Inline parsing
// ---------------------------------------------------------------------------

/** Parses a run of AsciiDoc inline markup into an inline AST. */
export function parseInline(text: string): AdocInline[] {
  return parseInlineSpan(text);
}

interface InlineDelimiter {
  char: string;
  tag: 'strong' | 'emphasis' | 'monospace' | 'superscript' | 'subscript' | 'highlight';
}

const INLINE_DELIMITERS: InlineDelimiter[] = [
  { char: '*', tag: 'strong' },
  { char: '_', tag: 'emphasis' },
  { char: '`', tag: 'monospace' },
  { char: '^', tag: 'superscript' },
  { char: '~', tag: 'subscript' },
  { char: '#', tag: 'highlight' },
];

function parseInlineSpan(text: string): AdocInline[] {
  const out: AdocInline[] = [];
  let buffer = '';
  let i = 0;

  const flushText = () => {
    if (buffer) {
      out.push({ tag: 'text', text: buffer });
      buffer = '';
    }
  };

  while (i < text.length) {
    const ch = text[i];

    // Hard break sentinel (see collectParagraphLines).
    if (ch === '\u0000') {
      flushText();
      out.push({ tag: 'break' });
      i += 1;
      continue;
    }

    // Wikilink: [[Target]] or [[Target,Display]].
    if (ch === '[' && text[i + 1] === '[') {
      const closeIdx = text.indexOf(']]', i + 2);
      if (closeIdx !== -1) {
        const inner = text.slice(i + 2, closeIdx);
        const [target, ...rest] = inner.split(',');
        const display = rest.join(',').trim();
        flushText();
        out.push({ tag: 'wikilink', target: target.trim(), display: display || undefined });
        i = closeIdx + 2;
        continue;
      }
    }

    // Inline image: image:URL[Alt].
    if (text.startsWith('image:', i) && text[i + 6] !== ':') {
      const m = text.slice(i).match(/^image:([^[\]\s][^[\]]*)\[([^\]]*)\]/);
      if (m) {
        flushText();
        out.push({ tag: 'image', src: m[1].trim(), alt: m[2].trim() });
        i += m[0].length;
        continue;
      }
    }

    // Explicit link macro: link:URL[Text].
    if (text.startsWith('link:', i)) {
      const m = text.slice(i).match(/^link:([^[\]\s][^[\]]*)\[([^\]]*)\]/);
      if (m) {
        flushText();
        const href = m[1].trim();
        const labelText = m[2].trim();
        // When no label is given, fall back to displaying the URL itself
        // — as a literal text node, NOT a recursive `parseInlineSpan`
        // call. Recursing on `href` would re-match this exact same
        // bare-URL pattern against the exact same string, infinitely
        // (a stack-overflowing loop that gets silently swallowed by
        // `parseAsciiDoc`'s catch-all, rendering the whole document
        // blank). See the identical guard on the autolink branch below.
        out.push({ tag: 'link', href, children: labelText ? parseInlineSpan(labelText) : [{ tag: 'text', text: href }] });
        i += m[0].length;
        continue;
      }
    }

    // Autolink: bare http(s):// URL, optionally with a `[Text]` suffix.
    if (/^https?:\/\//.test(text.slice(i))) {
      const m = text.slice(i).match(/^(https?:\/\/[^\s[\]]+)(\[([^\]]*)\])?/);
      if (m) {
        flushText();
        const href = m[1];
        const labelText = m[3]?.trim();
        // Same infinite-recursion hazard as the `link:` macro above: a
        // bare autolink with no `[label]` must NOT recurse into
        // `parseInlineSpan(href)` as its fallback label, since `href`
        // itself matches this exact branch again — recursing forever on
        // an unchanged input string.
        out.push({ tag: 'link', href, children: labelText ? parseInlineSpan(labelText) : [{ tag: 'text', text: href }] });
        i += m[0].length;
        continue;
      }
    }

    // Constrained emphasis delimiters: *strong*, _emphasis_, `mono`, ^sup^, ~sub~, #highlight#.
    // "Constrained" per AsciiDoc terminology: the opener must not be
    // followed by whitespace, and the closer must not be preceded by it.
    const delim = INLINE_DELIMITERS.find((d) => d.char === ch);
    if (delim && text[i + 1] !== ' ' && text[i + 1] !== undefined) {
      const closeIdx = findConstrainedCloser(text, i + 1, delim.char);
      if (closeIdx !== -1) {
        flushText();
        const inner = text.slice(i + 1, closeIdx);
        out.push({ tag: delim.tag, children: parseInlineSpan(inner) });
        i = closeIdx + 1;
        continue;
      }
    }

    buffer += ch;
    i += 1;
  }

  flushText();
  return out;
}

/** Finds the index of a constrained closing delimiter (not preceded by whitespace, not immediately after the opener). */
function findConstrainedCloser(text: string, from: number, char: string): number {
  for (let j = from; j < text.length; j++) {
    if (text[j] === char && text[j - 1] !== ' ' && j > from) {
      return j;
    }
  }
  return -1;
}

/** Plain-text content of a run of inline nodes (for alt text, heading ids, etc.). */
export function adocStringContent(nodes: AdocInline[]): string {
  let out = '';
  for (const node of nodes) {
    switch (node.tag) {
      case 'text':
        out += node.text;
        break;
      case 'break':
        out += '\n';
        break;
      case 'image':
        out += node.alt;
        break;
      case 'wikilink':
        out += node.display || node.target;
        break;
      default:
        out += adocStringContent(node.children);
    }
  }
  return out;
}
